/** @jsx jsx */
import React, { type ReactNode } from 'react';
import { styled, jsx, cssMap } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import {
	zIndex,
	columnHeaderHeight as headerHeight,
} from '../../../common/constants/styles/index.tsx';

export default function SwimlaneWrapper({
	flexibleColumns,
	minHeight,
	isVirtualised,
	hideTickMask, // Hide tick mask when the swimlane header is not rendered
	children,
}: {
	flexibleColumns?: boolean;
	minHeight?: number;
	isVirtualised?: boolean;
	hideTickMask?: boolean;
	children: ReactNode;
	isMatrixLayout?: boolean;
}) {
	const { isSoftwareFullTheming } = useSoftwareProjectTheming();

	return (
		<Wrapper
			flexibleColumns={flexibleColumns}
			isVirtualised={isVirtualised}
			// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={isVirtualised && minHeight ? { minHeight } : {}}
		>
			{!hideTickMask && <SwimlaneTickMask isThemed={isSoftwareFullTheming} />}
			{children}
		</Wrapper>
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div<{
	flexibleColumns?: boolean;
	isVirtualised?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: ({ isVirtualised }) => (isVirtualised ? 'block' : 'contents'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	position: ({ isVirtualised }) => (isVirtualised ? 'relative' : 'static'),
	willChange: 'transform',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ flexibleColumns }) => (flexibleColumns ? '100%' : 'auto'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const SwimlaneTickMaskOld = styled.div<{ isThemed: boolean }>(
	{
		position: 'sticky',
		top: 0,
		// marginTop needs to match header height

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		marginTop: `-${headerHeight}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		height: `${headerHeight}px`,

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		zIndex: zIndex.swimlaneTickMask,
		marginLeft: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		!props.isThemed && {
			backgroundColor: token('elevation.surface'),
		},
);
const swimlaneTickmaskStyle = cssMap({
	default: {
		position: 'sticky',
		top: 0,
		// Margin top needs to match header height which is 48px
		marginTop: `calc(${token('space.600')} * -1)`,
		height: token('space.600'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
		zIndex: zIndex.swimlaneTickMask,
		marginLeft: 0,
	},
	notThemed: {
		backgroundColor: token('elevation.surface'),
	},
});

const SwimlaneTickMask = componentWithFG(
	'tanuki_padding_margin_use_token',
	({ isThemed, children }: { isThemed: boolean; children?: ReactNode }) => {
		return (
			<div css={[swimlaneTickmaskStyle.default, !isThemed && swimlaneTickmaskStyle.notThemed]}>
				{children}
			</div>
		);
	},
	SwimlaneTickMaskOld,
);
